import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import Button from 'components/field/Button';
import Status from 'components/field/Status';
import StringInput from 'components/field/StringInput';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import LottiePlayer from 'components/global/LottiePlayer';
import Menu from 'components/global/Menu';
import Typography from 'components/global/Typography';

import useChangeLanguage from 'hooks/useChangeLanguage';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import { openAxios } from 'services/axios';
import { OPEN } from 'services/consts';

import { Breakpoints } from 'utils/const.styles';
import { languages } from 'utils/localization';

import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { faAngleDown, faArrowLeft, faArrowRight, faRotateLeft, faSearch } from '@fortawesome/pro-solid-svg-icons';

import * as S from './ShipmentTracking.styled';

const ShipmentTracking = () => {
	const [languageMenuButtonRef, setLanguageMenuButtonRef] = useState(null);
	const changeLanguage = useChangeLanguage();
	const localization = useSelector((state) => state.layout.localization);
	const isMobile = useMediaQuery(Breakpoints.mobileLandscape);
	const { t } = useTranslation();
	const { LanguageCode } = useLocale();

	const [searchParams, setSearchParams] = useSearchParams();
	const [trackingNumber, setTrackingNumber] = useState(searchParams.get('trackingNumber'));
	const [trackingNumberError, setTrackingNumberError] = useState(null);
	const [orderNumber, setOrderNumber] = useState(searchParams.get('orderNumber'));
	const [error, setError] = useState(null);
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(false);

	const isExternalLink = useMemo(() => searchParams.get('external') === 'true', [searchParams]);

	const language = useMemo(
		() =>
			({
				ar: {
					systemCode: 'arabicValue',
					code: 'ar_SA'
				},
				en: { systemCode: 'engValue', code: 'en_US' },
				tr: { systemCode: 'trValue', code: 'tr_TR' }
			})[searchParams.get('lang')],
		[searchParams]
	);

	useEffect(() => {
		if (language) changeLanguage(language.systemCode);
	}, [language]);

	useEffect(() => {
		if (trackingNumber && trackingNumberError) setTrackingNumberError(null);
	}, [trackingNumber]);

	const getTrackingInfo = async () => {
		if (!trackingNumber || trackingNumber === '') {
			setTrackingNumberError(t('alert.requiredField'));
			return;
		}
		setSearchParams({
			...(trackingNumber && { trackingNumber }),
			...(orderNumber && { orderNumber }),
			...(searchParams.get('lang') && { lang: searchParams.get('lang') })
		});
		setLoading(true);
		document.getElementById('trackingNumber').disabled = true;
		document.getElementById('orderNumber').disabled = true;
		const response = await openAxios.post(OPEN.GET_TRACKING_URL, {
			trackingNumber,
			orderID: orderNumber === '' || orderNumber === null ? undefined : orderNumber
		});
		if (response.data.length === 1) {
			setError(null);
			setData(response.data[0]);
		} else if (response.data.length > 1) {
			setError(1);
			setData(null);
		} else if (response.data.length === 0) {
			setError(2);
			setData(null);
		}
		document.getElementById('trackingNumber').disabled = false;
		document.getElementById('orderNumber').disabled = false;
		setLoading(false);
	};

	useEffect(() => {
		if (!trackingNumber || trackingNumber === '') return;
		getTrackingInfo();
	}, []);

	const firms = ['dhl', 'aramex', 'ups', 'fedex', 'smsa'];
	const isReset =
		trackingNumber &&
		searchParams.get('trackingNumber') === trackingNumber &&
		searchParams.get('orderNumber') === orderNumber;

	return (
		<S.OuterContainer>
			<S.Container>
				{!isExternalLink && (
					<S.Header>
						<S.Logo
							width={'10rem'}
							height={'10rem'}
							alt="logo"
							url={'https://storage.googleapis.com/tryoto-public/public/assets/images/otoLogo.png'}
							onClick={() => (window.location.href = 'https://www.tryoto.com')}
						/>
						<div>
							<Button variant="borderless" ref={setLanguageMenuButtonRef}>
								<Image width="20px" height="15px" url={localization.icon} alt="lang_icon" />
								<Typography>{localization.name}</Typography>
								<Icon icon={faAngleDown} />
							</Button>
							{languageMenuButtonRef && (
								<Menu
									buttonRef={languageMenuButtonRef}
									menuItems={languages.map((language) => ({
										label: language.name,
										img: language.icon,
										imgProps: { width: '20px', height: '15px' },
										selected: language.code === LanguageCode,
										action: async () => {
											setSearchParams({
												...(trackingNumber && { trackingNumber }),
												...(orderNumber && { orderNumber }),
												...(language.languageCode && { lang: language.languageCode })
											});
											await changeLanguage(language.code);
										}
									}))}
								/>
							)}
						</div>
					</S.Header>
				)}
				<S.MiddleContainer>
					<S.LeftContainer>
						<Typography noWrap={isMobile} css={S.HeaderCss}>
							{t('packageTracking.title')}
						</Typography>
						<S.InputContainer>
							<S.TextInputContainer>
								<Typography css={S.InputLabelCss}>
									{t('trackingNumber')} <span style={{ color: 'red' }}>*</span>
								</Typography>
								<StringInput
									description="trackingNumber.description"
									id="trackingNumber"
									value={trackingNumber}
									setValue={setTrackingNumber}
									errorMessage={trackingNumberError}
									placeholder="addTrackingNumber"
								/>
							</S.TextInputContainer>
							<S.TextInputContainer>
								<Typography css={S.InputLabelCss}>{t('orderNumber')}</Typography>
								<StringInput
									description="orderNumber.description"
									value={orderNumber}
									id="orderNumber"
									setValue={setOrderNumber}
									placeholder="addOrderNumber"
									style={{
										borderRadius: 'var(--small-border-radius) 0 0 var(--small-border-radius)'
									}}
								/>
							</S.TextInputContainer>
							<S.TextInputContainer>
								<Typography css={S.InputLabelCss}>&nbsp;</Typography>
								<Button
									onClick={() => {
										if (isReset) {
											setTrackingNumber(null);
											setOrderNumber(null);
											setSearchParams({});
											setError(null);
											setData(null);
										} else {
											getTrackingInfo();
										}
									}}
									icon={isReset ? faRotateLeft : faSearch}
									size="large"
									loading={loading}>
									{t(isReset ? 'reset' : 'search')}
								</Button>
							</S.TextInputContainer>
						</S.InputContainer>
						<Typography color="var(--content-3)">{t('youCanOnlyTrackOTO')}</Typography>
						{(error || data) && (
							<S.ResultContainer>
								{error && (
									<S.ErrorContainer>
										<Icon
											icon={faSearch}
											style={{
												backgroundColor: '#F1C40F',
												padding: '1.5rem',
												borderRadius: '50%',
												color: 'var(--white)'
											}}
										/>
										<S.ErrorWrapper>
											<Typography variant="p1" fontWeight="500">
												{error === 1 ? t('multipleOrderFound') : t('sms.notFoundTitle')}
											</Typography>
											<Typography color="var(--content-3)" noWrap={true}>
												{error === 1 ? t('fillOrderNumber') : t('checkYourTrackingNumber')}
											</Typography>
										</S.ErrorWrapper>
									</S.ErrorContainer>
								)}
								{data && data.trackingURL && (
									<S.ResultClickable
										onClick={() => {
											window.open(data.trackingURL, '_blank');
										}}>
										<S.ResultLeft>
											<Image url={data.icon} height={'5.5rem'} width={'5.5rem'} />
											<S.ErrorWrapper>
												<Typography variant="p1" fontWeight="500">
													{data.trackingNumber}
												</Typography>
												{data.sender && (
													<Typography color="var(--content-3)">
														{t('senderName')}: {data.sender}
													</Typography>
												)}
												<Typography color="var(--content-3)">
													{t('orderNumber')}: {data.orderID}
												</Typography>
											</S.ErrorWrapper>
										</S.ResultLeft>
										<Button
											icon={localization.code === 'arabicValue' ? faArrowLeft : faArrowRight}
											iconSize="large"
											iconButton={true}
											css={S.GoButton}
											size="x-large"
										/>
									</S.ResultClickable>
								)}
								{data && !data.trackingURL && (
									<S.Result>
										<S.ResultLeft>
											<Image url={data.icon} height={'5.5rem'} width={'5.5rem'} />
											<S.ErrorWrapper>
												<Typography variant="p1" fontWeight="500">
													{t('trackingNo')}:{' '}
													{data.trackingNumber ?? searchParams.get('trackingNumber')}
												</Typography>
												{data.sender && (
													<Typography color="var(--content-3)">
														{t('senderName')}: {data.sender}
													</Typography>
												)}
												<Typography color="var(--content-3)">
													{t('orderNumber')}: {data.orderID}
												</Typography>
											</S.ErrorWrapper>
										</S.ResultLeft>
										<Status value={data.status} />
									</S.Result>
								)}
							</S.ResultContainer>
						)}
						{!data && !error && (
							<S.LogoContainer>
								{firms.map((firm) => (
									<Image
										key={firm}
										url={`https://storage.googleapis.com/tryoto-public/tracking-system/dc/${firm}.png`}
										height="3.5rem"
										width="auto"
										style={{ filter: 'grayscale(100%) opacity(30%)' }}
									/>
								))}
							</S.LogoContainer>
						)}
					</S.LeftContainer>
					{!isMobile && !data && !error && (
						<S.RightContainer>
							<LottiePlayer name="animation" width="auto" height="auto" />
						</S.RightContainer>
					)}
				</S.MiddleContainer>

				{!isExternalLink && (
					<S.Footer>
						<Typography>OTO Global Inc. © 2025</Typography>
						<Button
							variant="borderless"
							onClick={() => {
								window.location.href = 'mailto:support@tryoto.com';
							}}>
							<Icon icon={faEnvelope}></Icon>
							<Typography>support@tryoto.com</Typography>
						</Button>
					</S.Footer>
				)}
			</S.Container>
		</S.OuterContainer>
	);
};

export default ShipmentTracking;
